// runService.js
// API calls for persisted run history, model-specific conversations,
// and execution results.

import { apiClient } from "./api";

const runService = {
  // Start a brand-new run/conversation thread for a codebase and model.
  startRun: async (
    userId,
    sessionToken,
    codebaseId,
    selectedModel
  ) => {
    console.log("🚀 startRun API call:", {
      userId,
      codebaseId,
      selectedModel,
    });

    try {
      const response = await apiClient.post(
        "/api/runs/start",
        {
          userId,
          session_token: sessionToken,
          codebaseId,
          selected_model: selectedModel,
        }
      );

      console.log(
        "✅ startRun API Success:",
        response.data
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ startRun API Error:",
        error
      );

      throw error;
    }
  },

  // Get the most recently updated run for a codebase,
  // regardless of model.
  //
  // Kept for backwards compatibility with existing code.
  getActiveRun: async (
    userId,
    sessionToken,
    codebaseId
  ) => {
    console.log("🚀 getActiveRun API call:", {
      userId,
      codebaseId,
    });

    try {
      const response = await apiClient.get(
        `/api/runs/active/${codebaseId}`,
        {
          params: {
            userId,
            session_token: sessionToken,
          },
        }
      );

      console.log(
        "✅ getActiveRun API Success"
      );

      return response.data.run;
    } catch (error) {
      console.error(
        "❌ getActiveRun API Error:",
        error
      );

      throw error;
    }
  },

  // Get the most recently updated run for a specific
  // codebase + model combination.
  //
  // This is what keeps Gemini, Qwen, DeepSeek, etc.
  // in separate conversations.
  getActiveRunForModel: async (
    userId,
    sessionToken,
    codebaseId,
    selectedModel
  ) => {
    console.log(
      "🚀 getActiveRunForModel API call:",
      {
        userId,
        codebaseId,
        selectedModel,
      }
    );

    try {
      const encodedModel =
        encodeURIComponent(selectedModel);

      const response = await apiClient.get(
        `/api/runs/active/${codebaseId}/${encodedModel}`,
        {
          params: {
            userId,
            session_token: sessionToken,
          },
        }
      );

      console.log(
        "✅ getActiveRunForModel API Success"
      );

      return response.data.run;
    } catch (error) {
      console.error(
        "❌ getActiveRunForModel API Error:",
        error
      );

      throw error;
    }
  },

  // Send a chat message within one specific run.
  //
  // The backend verifies that selectedModel matches the
  // model permanently assigned to this run.
  sendMessage: async (
    runId,
    userId,
    sessionToken,
    message,
    selectedModel
  ) => {
    console.log(
      "🚀 sendMessage API call:",
      {
        runId,
        selectedModel,
      }
    );

    try {
      const response = await apiClient.post(
        `/api/runs/${runId}/message`,
        {
          userId,
          session_token: sessionToken,
          message,
          selected_model: selectedModel,
        }
      );

      console.log(
        "✅ sendMessage API Success"
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ sendMessage API Error:",
        error
      );

      throw error;
    }
  },

  // Persist the latest execution result for a run.
  saveRunResult: async (
    runId,
    userId,
    sessionToken,
    resultPayload
  ) => {
    console.log(
      "🚀 saveRunResult API call:",
      {
        runId,
        ...resultPayload,
      }
    );

    try {
      const response = await apiClient.post(
        `/api/runs/${runId}/result`,
        {
          userId,
          session_token: sessionToken,
          ...resultPayload,
        }
      );

      console.log(
        "✅ saveRunResult API Success"
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ saveRunResult API Error:",
        error
      );

      throw error;
    }
  },

  // List all runs belonging to a codebase.
  //
  // Used by Run History and Compare Runs.
  listRunsForCodebase: async (
    userId,
    sessionToken,
    codebaseId
  ) => {
    console.log(
      "🚀 listRunsForCodebase API call:",
      {
        userId,
        codebaseId,
      }
    );

    try {
      const response = await apiClient.get(
        `/api/runs/codebase/${codebaseId}`,
        {
          params: {
            userId,
            session_token: sessionToken,
          },
        }
      );

      console.log(
        "✅ listRunsForCodebase API Success"
      );

      return response.data.runs;
    } catch (error) {
      console.error(
        "❌ listRunsForCodebase API Error:",
        error
      );

      throw error;
    }
  },

  // Get one complete run, including its conversation.
  getRun: async (
    runId,
    userId,
    sessionToken
  ) => {
    console.log("🚀 getRun API call:", {
      runId,
    });

    try {
      const response = await apiClient.get(
        `/api/runs/${runId}`,
        {
          params: {
            userId,
            session_token: sessionToken,
          },
        }
      );

      console.log(
        "✅ getRun API Success"
      );

      return response.data.run;
    } catch (error) {
      console.error(
        "❌ getRun API Error:",
        error
      );

      throw error;
    }
  },
};

export default runService;